'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ClientBrandConfig } from 'types/brand-config';

import { clientBrandConfigByHostname } from '@/config/brands';
import { httpStatus } from '@/config/http-status';

import { createMailToSupportHref } from '../../utils/create-mail-to-support-href';

import './page-error.css';

import { useSearchParams } from 'next/navigation';

type PageErrorProps = {
  statusMessage?: string;
  statusCode?: number;
  details?: string;
  showContactSupport?: boolean;
  links?: {
    brandKey?: keyof ClientBrandConfig['paths'];
    href?: string;
    label: string;
    includeSearchParams?: boolean; // This is done this way because I don't have access to the searchParams on all instances of the NotFound component
  }[];
  icon?: React.ReactNode;
  brand?: ClientBrandConfig; // Used by dev branches without a hostname brand lookup
};

export default function PageError(props: PageErrorProps) {
  const {
    statusMessage,
    statusCode,
    details,
    showContactSupport,
    links,
    icon,
    brand: brandProp,
  } = props;
  const searchParams = useSearchParams();
  const [hostname, setHostname] = useState<string | null>(null);
  const [brand, setBrand] = useState<ClientBrandConfig | null>(
    brandProp || null
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHostname(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    if (brandProp) {
      // use the passed-in override, for dev branches without a hostname brand lookup
      return;
    }
    if (hostname) {
      try {
        const brandConfig = clientBrandConfigByHostname(hostname);
        setBrand(brandConfig);
      } catch (error) {
        console.error('Brand lookup failed', {
          hostname,
          error: error instanceof Error ? error.message : String(error),
        });
        setBrand(null);
      }
    }
  }, [hostname, brandProp]);

  let message = statusMessage;
  if (!message) {
    // no message supplied, look up a status based message, or use default
    if (typeof statusCode === 'number' && httpStatus[statusCode]) {
      message = httpStatus[statusCode];
    } else {
      message = 'Oops!'; // default
    }
  }

  return (
    <>
      <main className="mx-auto h-full max-w-4xl px-4">
        <div className="mb-2 mt-12 grid w-full lg:mt-6 lg:grid-cols-2">
          {/* Left */}
          <div className="flex flex-col justify-between gap-4 lg:mt-16">
            <div className="flex flex-col justify-start gap-4">
              <h1 className="flex w-fit text-left text-5xl font-normal normal-case tracking-tight text-gray-9 lg:!text-8xl">
                {message}
              </h1>
              {details && (
                <h2 className="!text-2xl leading-7 text-gray-8">{details}</h2>
              )}
              {brand && links && (
                <ul>
                  <label className="text-[1rem] text-gray-8">
                    These links may help:
                  </label>
                  {links?.map((link, index) => {
                    const search = link.includeSearchParams
                      ? new URLSearchParams(searchParams)
                      : undefined;
                    const rootPath = link.brandKey
                      ? brand.paths
                        ? (brand.paths[link.brandKey] as string)
                        : '/'
                      : link.href || '/';
                    const path = search
                      ? `${rootPath}?${search.toString()}`
                      : rootPath;
                    return (
                      <li key={`page-error-link-${index}`}>
                        <Link
                          href={path}
                          className="flex flex-nowrap items-center text-sm text-secondary-main hover:text-secondary-light"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M14.41 18.16L8.75 12.5l5.66-5.66l.7.71l-4.95 4.95l4.95 4.95z"
                            />
                          </svg>
                          {link.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!brand && (
                <ul>
                  <label className="text-[1rem] text-gray-8">
                    This link may help:
                  </label>
                  <li>
                    <Link
                      href="/"
                      className="flex flex-nowrap items-center text-sm text-secondary-main hover:text-secondary-light"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          d="M14.41 18.16L8.75 12.5l5.66-5.66l.7.71l-4.95 4.95l4.95 4.95z"
                        />
                      </svg>
                      Home
                    </Link>
                  </li>
                </ul>
              )}
            </div>
            {brand && showContactSupport && (
              <Link
                href={createMailToSupportHref(brand, `Booking Support`)}
                className="text-[1rem] font-semibold text-secondary-main hover:text-secondary-lighter"
              >
                Contact Support{' '}
                <span aria-hidden="true" className="text-inherit">
                  &rarr;
                </span>
              </Link>
            )}
          </div>
          {/* Right */}
          <div className="svg-container flex flex-col items-center gap-4">
            {!icon && (
              <>
                {statusCode === 404 && <NotFoundSVG />}
                {statusCode === 503 && <NetworkLostSVG />}
                {(!statusCode || ![404, 503].includes(statusCode)) && (
                  <DefaultSVG />
                )}
              </>
            )}
            {icon && (
              <div className="lg:ml-6 lg:mt-10 lg:flex lg:flex-row lg:justify-end lg:self-end">
                {icon}
              </div>
            )}
            {statusCode && (
              <p className="self-end text-right text-sm font-semibold text-gray-7">
                Error code: {statusCode}
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

const DefaultSVG = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-auto w-full max-w-sm text-gray-8"
      viewBox="0 0 1024 1024"
    >
      <path
        fill="currentColor"
        d="M688 312v-48c0-4.4-3.6-8-8-8H296c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h384c4.4 0 8-3.6 8-8m-392 88c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h184c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zm376 116c-119.3 0-216 96.7-216 216s96.7 216 216 216s216-96.7 216-216s-96.7-216-216-216m107.5 323.5C750.8 868.2 712.6 884 672 884s-78.8-15.8-107.5-44.5S520 772.6 520 732s15.8-78.8 44.5-107.5S631.4 580 672 580s78.8 15.8 107.5 44.5S824 691.4 824 732s-15.8 78.8-44.5 107.5M640 812a32 32 0 1 0 64 0a32 32 0 1 0-64 0m12-64h40c4.4 0 8-3.6 8-8V628c0-4.4-3.6-8-8-8h-40c-4.4 0-8 3.6-8 8v112c0 4.4 3.6 8 8 8M440 852H208V148h560v344c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V108c0-17.7-14.3-32-32-32H168c-17.7 0-32 14.3-32 32v784c0 17.7 14.3 32 32 32h272c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8"
      />
    </svg>
  );
};

const NotFoundSVG = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-auto w-full max-w-sm lg:w-[50dvh]"
    >
      {/* Top rectangle */}
      <path
        className="fill-primary-main/50"
        fill="currentColor"
        d="m9.107 10.559l-.899-1.384l-.898-1.384l-.9-1.383l1.385-.9l3.46-2.246l1.383-.899l.9 1.384l.898 1.384l.899 1.384l-1.384.899l-3.46 2.247z"
      />
      {/* Outter Triangle */}
      <path
        className="fill-secondary-main/55"
        fill="currentColor"
        d="m13.577 14.622l1.697.865l-.1-1.903l-.188-3.61l-.1-1.902l-1.598 1.038l-3.032 1.968l-1.598 1.038l1.698.865z"
      />
      {/* Right rectangle */}
      <path
        className="fill-tertiary-light"
        fill="currentColor"
        d="M21.86 10.314v11.551h-5.775v-11.55z"
      />
      {/* Dash on left */}
      <path
        className="fill-primary-main/75"
        fill="currentColor"
        d="m8.208 11.012l-3.76 10.922l-1.56-.537l3.76-10.922z"
      />
      {/* Circle */}
      <path
        className="fill-primary-main"
        fill="currentColor"
        d="M14.434 18.152a3.713 3.713 0 1 1-7.425 0a3.713 3.713 0 0 1 7.425 0z"
      />
    </svg>
  );
};

const NetworkLostSVG = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-auto w-full max-w-sm text-gray-8 lg:w-[50dvh]"
      viewBox="0 0 24 24"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M8.5 16c.867-.867 1.92-1.358 3-1.474m-6-2.026c3.173-3.173 7.122-3.83 10.5-1.968M2 8.5c6.316-5.333 13.684-5.333 20 0m-1 5l-6 6m6 0l-6-6"
        color="currentColor"
      />
    </svg>
  );
};
